import { createTransport } from './transport.js'
import { createAuthApi } from './domains/auth.js'
import { createCliAuthorizationApi } from './domains/cli-authorization.js'
import { createDramaApi } from './domains/drama.js'
import { createFeedApi } from './domains/feed.js'
import { createPayApi } from './domains/pay.js'
import { createAccountApi } from './domains/account.js'
import { createStudioApi } from './domains/studio.js'
import { createVisualGameApi } from './domains/visual-game.js'
import { createStoryFilmLibraryApi } from './domains/story-film-library.js'
import { createStoryFilmPublicApi } from './domains/story-film-public.js'
import { createStoryFilmPolicyApi } from './domains/story-film-policy.js'
import { createStoryFilmProductionApi } from './domains/story-film-production.js'
import { createNovelApi } from './domains/novel/index.js'
import { createWaitlistApi } from './domains/waitlist.js'
import { createCommerceApi } from './domains/commerce.js'
import { createH3liveApi } from './domains/h3live.js'

/**
 * @typedef {import('./transport.js').TokenStore} TokenStore
 */

/**
 * @typedef {object} ApiClientOpts
 * @property {string} baseUrl `@app/server` 根 URL（注入；包内不读 env）
 * @property {TokenStore} tokenStore token 读写抽象（由 `@drama/auth-client` 提供实现）
 * @property {() => void} [onAuthExpired] 二次 401 后回调
 * @property {string} [deviceId] 缺省由 tokenStore.getDeviceId() 提供
 * @property {() => string} [getLocale] x-locale 取值，缺省 'en'
 * @property {typeof fetch} [fetchImpl] 测试注入用，缺省 globalThis.fetch
 */

/**
 * 唯一入口工厂：装配 transport + 各后端域模块，并在创建时校验 opts（装配时崩）。
 * @param {ApiClientOpts} opts
 */
export function createApiClient(opts) {
  if (!opts || typeof opts !== 'object') {
    throw new Error('createApiClient: opts is required')
  }
  if (typeof opts.baseUrl !== 'string') {
    throw new Error('createApiClient: opts.baseUrl must be a string ("" = same-origin relative paths via proxy)')
  }
  if (!opts.tokenStore || typeof opts.tokenStore.getAccessToken !== 'function') {
    throw new Error('createApiClient: opts.tokenStore (with getAccessToken/getDeviceId/refresh) is required')
  }
  if (typeof opts.tokenStore.getDeviceId !== 'function' || typeof opts.tokenStore.refresh !== 'function') {
    throw new Error('createApiClient: opts.tokenStore must implement getDeviceId() and refresh()')
  }

  const fetchImpl = opts.fetchImpl ?? globalThis.fetch
  if (typeof fetchImpl !== 'function') {
    throw new Error('createApiClient: no fetch available; inject opts.fetchImpl')
  }

  const transport = createTransport({
    baseUrl: opts.baseUrl,
    tokenStore: opts.tokenStore,
    onAuthExpired: opts.onAuthExpired,
    getLocale: opts.getLocale,
    deviceId: opts.deviceId,
    fetchImpl,
  })

  return {
    auth: createAuthApi(transport),
    cliAuthorization: createCliAuthorizationApi(transport),
    drama: createDramaApi(transport),
    feed: createFeedApi(transport),
    pay: createPayApi(transport),
    account: createAccountApi(transport),
    studio: createStudioApi(transport),
    storyFilmLibrary: createStoryFilmLibraryApi(transport),
    storyFilmPolicy: createStoryFilmPolicyApi(transport),
    storyFilmPublic: createStoryFilmPublicApi(transport, { baseUrl: opts.baseUrl }),
    storyFilmProduction: createStoryFilmProductionApi(transport),
    visualGame: createVisualGameApi(transport),
    novel: createNovelApi(transport),
    waitlist: createWaitlistApi(transport),
    commerce: createCommerceApi(transport),
    h3live: createH3liveApi(transport),
  }
}
